import { createCombinedAbortSignal } from './abortSignalUtils';
import { getGlobal } from './apiClients/global';
import { deriveAuthFromDomain, getBffGateway } from './chirp-bff';
import { createBffRequestName, getBaseUrlForAuth, getInternalAuthToken } from './quick-fetch-utils';
const FIVE_MINUTES_MS = 5 * 60 * 1000;
function getBffEarlyStreamsCache() {
  const g = getGlobal();
  if (!g.__chirpBffEarlyStreams) {
    g.__chirpBffEarlyStreams = new Map();
  }
  return g.__chirpBffEarlyStreams;
}
export function getBffEarlyStreamEntry(key) {
  return getBffEarlyStreamsCache().get(key);
}
export function removeBffEarlyStreamEntry(key) {
  getBffEarlyStreamsCache().delete(key);
}

/**
 * Returns the deterministic early-request name for a streaming BFF.
 * Hashes `{bffHash, name, args}` — identical to `getBffEarlyRequestName`
 * for non-streaming BFFs. Does NOT honor `earlyRequestName` overrides so
 * the result is always stable, matching the style of the Avro streaming
 * equivalent `createRequestName`.
 */
export function getStreamingBffEarlyRequestName(details, inputs) {
  var _ref;
  const {
    args
  } = (_ref = inputs) !== null && _ref !== void 0 ? _ref : {};
  return createBffRequestName(details, args);
}

/**
 * Non-destructive peek — returns the cached early stream entry without removing it.
 */
export function getStreamingBffEarlyStreamEntryFor(details, inputs) {
  var _ref2;
  const {
    earlyRequestName,
    args
  } = (_ref2 = inputs) !== null && _ref2 !== void 0 ? _ref2 : {};
  const key = earlyRequestName !== null && earlyRequestName !== void 0 ? earlyRequestName : createBffRequestName(details, args);
  return getBffEarlyStreamEntry(key);
}

/**
 * Destructive consume — returns the cached early stream entry and removes it
 * from the cache so it can only be consumed once.
 */
export function consumeStreamingBffEarlyStreamEntry(details, inputs) {
  var _ref3;
  const {
    earlyRequestName,
    args
  } = (_ref3 = inputs) !== null && _ref3 !== void 0 ? _ref3 : {};
  const key = earlyRequestName !== null && earlyRequestName !== void 0 ? earlyRequestName : createBffRequestName(details, args);
  const entry = getBffEarlyStreamEntry(key);
  if (entry) {
    removeBffEarlyStreamEntry(key);
    return entry;
  }
  return undefined;
}

/**
 * Starts an early streaming BFF request and stores the Response promise in a
 * global cache. Does NOT consume the response body — consumers attach later
 * and stream from wherever the network left off.
 *
 * This is the streaming-BFF analog of `makeChirpEarlyStreamRequest` for Avro
 * streaming RPCs. Because no application-level event buffering is needed
 * (pre-attach SSE bytes remain in the browser's ReadableStream buffer), the
 * function is synchronous — unlike the Avro version, BFFs always route through
 * the gateway so there is no `resolveLocalRequest` await.
 *
 * @param fetcher - A `fetch`-compatible function. Pass the same fetcher that
 *   `createStreamingBffClient` uses so auth headers and timeouts are consistent.
 *   This parameter keeps the module external-package-free (no `quick-fetch` or
 *   `chirp-fetch` import), satisfying the npm-shim import restriction.
 */
export function makeBffEarlyStreamRequest(fetcher, details, inputs) {
  var _ref4, _details$target, _location$hostname, _location, _getBaseUrlForAuth;
  if (!details.isStreaming) {
    throw new Error('makeBffEarlyStreamRequest may only be used for streaming BFFs');
  }
  const {
    earlyRequestName,
    timeout,
    portalId,
    userId,
    args
  } = (_ref4 = inputs) !== null && _ref4 !== void 0 ? _ref4 : {};
  const key = earlyRequestName !== null && earlyRequestName !== void 0 ? earlyRequestName : createBffRequestName(details, args);
  const existing = getBffEarlyStreamEntry(key);
  if (existing) {
    // Idempotent: a second call with the same key returns the cached entry
    // without firing a second network request.
    return {
      key,
      entry: existing
    };
  }
  const auth = (_details$target = details.target) !== null && _details$target !== void 0 ? _details$target : deriveAuthFromDomain((_location$hostname = (_location = getGlobal().location) === null || _location === void 0 ? void 0 : _location.hostname) !== null && _location$hostname !== void 0 ? _location$hostname : '');
  const {
    bffHash,
    name
  } = details;
  const gatewayPath = `${getBffGateway(auth)}/execute/hash/${bffHash}/${name}`;
  const gatewayUrl = `${(_getBaseUrlForAuth = getBaseUrlForAuth(auth)) !== null && _getBaseUrlForAuth !== void 0 ? _getBaseUrlForAuth : ''}${gatewayPath}`;
  const streamTimeout = typeof timeout === 'number' ? timeout : FIVE_MINUTES_MS;
  const request = {
    args: [args !== null && args !== void 0 ? args : {}]
  };

  // eslint-disable-next-line compat/compat
  const abortController = new AbortController();
  const combinedSignal = createCombinedAbortSignal(streamTimeout, abortController.signal);
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream'
  };
  if (auth === 'internal') {
    const internalAuthToken = getInternalAuthToken();
    if (internalAuthToken) {
      headers['Authorization'] = `Bearer ${internalAuthToken}`;
    }
  }

  // Early requests always run in-browser ('bend' context, not npmShim),
  // so X-Origin-HubId is only set when auth is 'internal' (matching the
  // 'bend' branch of the consumer's header logic in createBffStreamHandler).
  if (portalId !== undefined && auth === 'internal') {
    headers['X-Origin-HubId'] = String(portalId);
  }
  if (userId !== undefined) {
    headers['X-Origin-UserId'] = String(userId);
  }

  // Fire the request but do NOT await or read the body.
  // SSE bytes that arrive before the consumer attaches accumulate in the
  // browser's ReadableStream/OS network buffer, so no app-level replay is needed.
  const responsePromise = fetcher(`${gatewayUrl}?clienttimeout=${streamTimeout}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
    signal: combinedSignal
  });
  const entry = {
    response: responsePromise,
    abortController,
    startTime: Date.now()
  };
  getBffEarlyStreamsCache().set(key, entry);
  return {
    key,
    entry
  };
}